import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, CalendarDays, Flame, Users } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Marquee } from "@/components/Marquee";
import { Reveal, Overline } from "@/components/Reveal";

const IMGS = [
  "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=1200&q=80",
];

const ICONS = { flame: Flame, users: Users, book: BookOpen, calendar: CalendarDays };

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center bg-black overflow-hidden" data-testid="hero">
        <div className="absolute -top-32 right-[-10%] w-[560px] h-[560px] rounded-full bg-ember/10 blur-[160px]" aria-hidden="true" />
        <div className="absolute bottom-0 left-[-10%] w-[420px] h-[420px] rounded-full bg-white/5 blur-[140px]" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-24 w-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border border-white/25 mb-10 shadow-[0_0_60px_rgba(0,217,255,0.25)]"
          >
            <img src="/logo.jpg" alt="Movimiento Church" className="w-full h-full object-cover scale-[1.65]" data-testid="hero-logo" />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.35em] text-ember mb-8"
            data-testid="hero-overline"
          >
            {t.hero.overline}
          </motion.p>

          <h1 className="font-display font-extrabold leading-[0.9] tracking-[-0.04em] text-[clamp(1.65rem,7.6vw,9rem)] uppercase">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="glitch block text-white"
                data-text={t.hero.titleA}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {t.hero.titleA}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="glitch block text-stroke"
                data-text={t.hero.titleB}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {t.hero.titleB}
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="text-xl md:text-2xl text-white/70 max-w-xl mt-8 font-light"
            data-testid="hero-sub"
          >
            {t.hero.sub}
          </motion.p>

          <motion.blockquote
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-8 max-w-xl"
            data-testid="hero-verse"
          >
            <p className="text-white/60 italic leading-relaxed">{t.hero.verse}</p>
            <cite className="not-italic text-xs uppercase tracking-[0.3em] text-ember mt-3 block">
              {t.hero.verseRef}
            </cite>
          </motion.blockquote>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.25em] text-white/40 mt-6"
            data-testid="hero-badge"
          >
            {t.hero.badge}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="flex flex-wrap gap-4 mt-12"
          >
            <button
              data-testid="hero-cta-join"
              onClick={() => navigate("/contacto")}
              className="group flex items-center gap-3 bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-10 py-5 hover:bg-ember hover:shadow-[0_0_50px_rgba(0,217,255,0.5)] transition-all duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-visit"
              onClick={() => navigate("/eventos")}
              className="flex items-center gap-3 border border-white/40 text-white text-xs uppercase tracking-[0.2em] rounded-full px-10 py-5 hover:border-ember hover:text-ember transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </section>

      <Marquee items={t.marquee} />

      {/* QUIÉNES SOMOS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="quienes-somos">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
          <Reveal className="md:col-span-7">
            <Overline>{t.quienes.overline}</Overline>
            <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight mt-6 leading-[0.95]">
              {t.quienes.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5 flex items-end">
            <p className="text-white/60 leading-relaxed text-lg border-l border-ember/40 pl-8">
              {t.quienes.text}
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {IMGS.map((img, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div className="group relative overflow-hidden h-80 md:h-[28rem] border border-white/10">
                <img
                  src={img}
                  alt={t.quienes.labels[i]}
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-black/55 group-hover:bg-black/35 transition-colors duration-700" />
                <p className="absolute bottom-6 left-6 text-xs uppercase tracking-[0.3em] text-white font-semibold">
                  <span className="text-ember mr-2">0{i + 1}</span>
                  {t.quienes.labels[i]}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EXPERIENCIA / HORARIOS */}
      <section className="border-y border-white/10 bg-coal" data-testid="experiencia">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Overline>{t.experiencia.overline}</Overline>
            <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight mt-6 mb-16">
              {t.experiencia.title}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {t.experiencia.cards.map((card, i) => {
              const Icon = ICONS[card.icon];
              return (
                <Reveal key={card.title} delay={i * 0.08}>
                  <button
                    onClick={() => navigate(card.link)}
                    data-testid={`experiencia-card-${card.icon}`}
                    className="group w-full text-left bg-black border border-white/15 p-10 h-full hover:border-ember/70 hover:shadow-[0_0_40px_rgba(0,217,255,0.12)] hover:-translate-y-1 transition-all duration-500"
                  >
                    <Icon size={26} className="text-ember mb-8" />
                    <h3 className="font-display text-3xl font-bold uppercase mb-3">{card.title}</h3>
                    <p className="text-white/55 leading-relaxed mb-8">{card.text}</p>
                    <p className="text-xs uppercase tracking-[0.25em] text-ember/90">{card.time}</p>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="testimonials">
        <Reveal>
          <Overline>{t.testimonials.overline}</Overline>
          <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tight mt-6 mb-16">
            {t.testimonials.title}
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.testimonials.items.map((item, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <figure className="border border-white/10 bg-black p-10 h-full flex flex-col hover:border-white/25 transition-colors duration-500" data-testid={`testimonial-${i}`}>
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-16 h-16 rounded-full object-cover grayscale border border-white/20 mb-8"
                />
                <blockquote className="text-white/80 leading-relaxed text-lg mb-8 flex-1">
                  “{item.quote}”
                </blockquote>
                <figcaption>
                  <p className="text-ember font-semibold">{item.name}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-1">{item.tag}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative border-t border-white/10 bg-black overflow-hidden" data-testid="final-cta">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-ember/10 blur-[180px]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40 text-center">
          <Reveal>
            <h2 className="font-display text-[clamp(2rem,7vw,6rem)] font-extrabold uppercase tracking-tight leading-[0.95] mb-8">
              {t.finalCta.title}
            </h2>
            <p className="text-white/60 text-lg max-w-xl mx-auto mb-14">{t.finalCta.text}</p>
            <button
              data-testid="final-cta-btn"
              onClick={() => navigate("/contacto")}
              className="group inline-flex items-center gap-3 bg-ember text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full px-12 py-6 shadow-[0_0_70px_rgba(0,217,255,0.45)] hover:bg-white hover:shadow-[0_0_90px_rgba(255,255,255,0.35)] transition-all duration-300"
            >
              {t.finalCta.cta}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
